import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiBase';
import { toast } from 'react-toastify';
import { getTenantSlug } from '../utils/tenant'; // [TENANT]

/**
 * Unified API Client
 * Consolidated from apiClient.js and axiosInstance.js
 * Simple, clean, and efficient API management
 */

const DEFAULT_API_TIMEOUT =
  parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || 300000;

const LARGE_UPLOAD_MAX_BYTES = 1024 * 1024 * 1024; // 1GB

const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  timeout: DEFAULT_API_TIMEOUT,
  maxContentLength: LARGE_UPLOAD_MAX_BYTES,
  maxBodyLength: LARGE_UPLOAD_MAX_BYTES,
  headers: {
    Accept: 'application/json',
  },

  // Ensure CORS preflight is handled
  validateStatus: function (status) {
    return status >= 200 && status < 300;
  },
});

// Request interceptor - Add auth token and handle content types
apiClient.interceptors.request.use(
  (config) => {
    // [TENANT] Tell the backend which client subdomain this is.
    // In production the backend reads the Host header instead; this header is
    // only used in local dev (igi.localhost:3000 -> proxy rewrites Host).
    const tenantSlug = getTenantSlug();
    if (tenantSlug) {
      config.headers['X-Tenant-Slug'] = tenantSlug;
    }

    // Skip token for auth and public requests
    const publicEndpoints = [
      '/auth/login',
      '/auth/google',
      '/auth/microsoft',
      '/auth/setup-2fa',
      '/auth/verify-setup',
      '/auth/verify-otp',
      '/auth/refresh-token',
      '/landing-page',
      '/public/tenant', // [TENANT]
    ];

    const isPublicRequest = publicEndpoints.some((endpoint) =>
      config.url?.includes(endpoint)
    );

    if (!isPublicRequest) {
      const token = localStorage.getItem('accessToken');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Large file uploads (SCORM ZIPs, avatars, etc.)
    // should not time out on slow connections
    if (config.data instanceof FormData) {
      if (
        config.timeout === undefined ||
        config.timeout === DEFAULT_API_TIMEOUT
      ) {
        config.timeout = 0;
      }

      config.maxContentLength = LARGE_UPLOAD_MAX_BYTES;
      config.maxBodyLength = LARGE_UPLOAD_MAX_BYTES;

      // Let axios set the Content-Type for FormData
      // because it needs to include the multipart boundary.
      delete config.headers['Content-Type'];
    } else {
      // Set JSON content type for regular requests
      config.headers['Content-Type'] = 'application/json';
    }

    // Log only in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log(
        'API Request:',
        config.method?.toUpperCase(),
        config.url
      );
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// [TENANT] Clears every stored credential
const clearAuthStorage = () => {
  const authKeys = [
    'accessToken',
    'refreshToken',
    'user',
    'token',
    'auth_token',
    'jwt_token',
  ];

  authKeys.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

// Response interceptor - Handle token refresh and timeouts
apiClient.interceptors.response.use(
  (response) => {
    // Log only in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log(
        'API Response:',
        response.config?.method?.toUpperCase(),
        response.config?.url,
        response.status
      );
    }

    return response;
  },

  async (error) => {
    // Log errors
    console.error(
      'API Error:',
      error.config?.method?.toUpperCase(),
      error.config?.url,
      error.response?.status,
      error.message
    );

    const originalRequest = error.config;

    // Handle timeout errors with better messaging
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout:', error.config?.url);

      toast.error(
        'Request timed out. Please check your connection and try again.',
        {
          position: 'top-center',
          autoClose: 5000,
        }
      );

      return Promise.reject(error);
    }

    // Handle network errors
    if (error.message === 'Network Error') {
      console.error('Network error:', error.config?.url);

      toast.error(
        'Network error. Please check if the backend server is running.',
        {
          position: 'top-center',
          autoClose: 5000,
        }
      );

      return Promise.reject(error);
    }

    // SINGLE SESSION: account was logged in elsewhere, so sign out immediately
    if (error.response?.data?.code === 'SESSION_REPLACED') {
      clearAuthStorage();

      // toastId prevents duplicate toasts when several requests fail at once
      toast.error(error.response.data.message, {
        toastId: 'session-replaced',
      });

      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }

      return Promise.reject(error);
    }

    // [TENANT] A token from another client's domain was rejected by the backend.
    // Sign out locally and send the user to this domain's login page.
    if (
      error.response?.status === 403 &&
      error.response?.data?.message === 'Access denied for this organization'
    ) {
      clearAuthStorage();

      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }

      return Promise.reject(error);
    }

    // [FEATURES] A feature was switched off for this organization while the
    // page was open. Tell FeaturesProvider to reload so the UI for it
    // disappears instead of showing errors.
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'FEATURE_DISABLED'
    ) {
      window.dispatchEvent(new Event('features:refresh'));
      return Promise.reject(error);
    }

    // Handle 401 errors with token refresh
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      console.log(
        '🔐 401 Error detected, attempting token refresh...'
      );

      console.log('🔐 Request URL:', originalRequest.url);
      console.log('🔐 Request method:', originalRequest.method);

      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');

        console.log(
          '🔐 Refresh token exists:',
          !!refreshToken
        );

        if (refreshToken) {
          // [TENANT] This call uses plain axios, so it must send the tenant header itself
          const tenantSlug = getTenantSlug();

          const refreshResponse = await axios.post(
            `${getApiBaseUrl()}/auth/refresh-token`,
            {
              refresh_token: refreshToken,
            },
            {
              withCredentials: true,
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                ...(tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {}), // [TENANT]
              },
            }
          );

          if (refreshResponse.data.success) {
            const { access_token } = refreshResponse.data.data;

            localStorage.setItem('accessToken', access_token);

            originalRequest.headers.Authorization =
              `Bearer ${access_token}`;

            return apiClient(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error(
          '❌ Token refresh failed:',
          refreshError
        );

        console.error('❌ Refresh error details:', {
          message: refreshError.message,
          status: refreshError.response?.status,
          data: refreshError.response?.data,
        });

        clearAuthStorage();

        // SINGLE SESSION: the refresh call itself can return SESSION_REPLACED
        // (it uses plain axios, so it doesn't pass through this interceptor)
        if (refreshError.response?.data?.code === 'SESSION_REPLACED') {
          toast.error(refreshError.response.data.message, {
            toastId: 'session-replaced',
          });

          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }

          return Promise.reject(refreshError);
        }

        const pathname = window.location.pathname || '';

        const onPublicAuthScreen =
          /^\/(login|2fa\/(setup|verify))(\/|$)/.test(
            pathname
          );

        const requestUrl = originalRequest?.url || '';

        const isAuthApiCall =
          /\/auth\/(login|setup-2fa|verify-setup|verify-otp|refresh-token)/.test(
            requestUrl
          );

        if (
          !onPublicAuthScreen &&
          !isAuthApiCall &&
          pathname !== '/login'
        ) {
          window.location.href = '/login';

          toast.error(
            'Session expired. Please login again.'
          );
        }
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Client-side CSV export helper.
 *
 * Example:
 *
 * api.exportCsv(
 *   'manager-team-report',
 *   rows,
 *   ['name', 'email', 'course', 'progress']
 * );
 */
const exportToCsv = (filename, rows = [], columns = null) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    toast.info('No data available to export.', {
      position: 'top-center',
      autoClose: 3000,
    });

    return false;
  }

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) {
      return '';
    }

    let normalizedValue = value;

    if (typeof value === 'object') {
      try {
        normalizedValue = JSON.stringify(value);
      } catch {
        normalizedValue = String(value);
      }
    }

    const stringValue = String(normalizedValue);

    if (
      stringValue.includes(',') ||
      stringValue.includes('"') ||
      stringValue.includes('\n') ||
      stringValue.includes('\r')
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  };

  const normalizedFilename = String(filename || 'export')
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '-')
    .replace(/\.csv$/i, '');

  const headers =
    Array.isArray(columns) && columns.length > 0
      ? columns
      : Object.keys(rows[0]);

  const csvRows = [
    headers.map((header) => escapeCsvValue(header)).join(','),
    ...rows.map((row) =>
      headers.map((header) => escapeCsvValue(row?.[header])).join(',')
    ),
  ];

  // UTF-8 BOM helps Excel correctly recognize UTF-8 CSV files.
  const csvContent = '\uFEFF' + csvRows.join('\r\n');

  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;',
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `${normalizedFilename}.csv`;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(url);

  return true;
};

// Small helper: build "?a=1&b=2" (or "") from a params object
const qsOf = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return qs ? `?${qs}` : '';
};

const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };

/**
 * API Methods - Clean and simple
 */

const api = {
  exportCsv: exportToCsv,

  // [TENANT] Public endpoints (no login needed)
  public: {
    // Name, logo and brand colours of the client for the current subdomain.
    // Returns { data: null } on the main site, 404 for an unknown subdomain.
    getTenant: () => apiClient.get('/public/tenant'),
  },

  // Authentication
  auth: {
    login: (email, password) =>
      apiClient.post('/auth/login', { email, password }),

    googleLogin: (credential) =>
      apiClient.post('/auth/google', { credential }),

    microsoftLogin: (idToken) =>
      apiClient.post('/auth/microsoft', { credential: idToken }),

    setup2fa: (tempToken, { regenerate = false } = {}) =>
      apiClient.post('/auth/setup-2fa', {
        temp_token: tempToken,
        ...(regenerate ? { regenerate: true } : {}),
      }),

    verify2faSetup: ({ tempToken, otp }) =>
      apiClient.post('/auth/verify-setup', { temp_token: tempToken, otp }),

    verifyOtp: ({ tempToken, otp, backupCode }) =>
      apiClient.post('/auth/verify-otp', {
        temp_token: tempToken,
        ...(otp ? { otp } : {}),
        ...(backupCode ? { backup_code: backupCode } : {}),
      }),

    logout: () => apiClient.post('/auth/logout'),

    validateToken: () => apiClient.get('/auth/validate-token'),

    refreshToken: (refreshToken) =>
      apiClient.post('/auth/refresh-token', { refresh_token: refreshToken }),

    forgotPassword: (email) =>
      apiClient.post('/auth/forgot-password', { email }),

    verifyResetToken: (token) =>
      apiClient.get(`/auth/verify-reset-token/${token}`),

    resetPassword: ({ token, password, confirmPassword }) =>
      apiClient.post('/auth/reset-password', {
        token,
        password,
        confirm_password: confirmPassword,
      }),

    getProfile: () => apiClient.get('/auth/profile'),

    // Update current user profile
    updateProfile: (formData) =>
      apiClient.put('/auth/profile', formData, multipart),

    // Change current user's password
    changePassword: (currentPassword, newPassword, confirmPassword) =>
      apiClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),
  },

  // Users
  users: {
    getAll: () => apiClient.get('/users'),
    getById: (id) => apiClient.get(`/users/${id}`),
    create: (userData) => apiClient.post('/users', userData),
    update: (id, userData) => apiClient.put(`/users/${id}`, userData),
    delete: (id) => apiClient.delete(`/users/${id}`),
    restore: (id) => apiClient.put(`/users/${id}/restore`),
    set2faEnabled: (id, enabled) =>
      apiClient.patch(`/users/${id}/2fa`, { enabled: Boolean(enabled) }),
    bulkValidate: (users) => apiClient.post('/users/bulk/validate', { users }),
    bulkImport: (users) => apiClient.post('/users/bulk/import', { users }),
  },

  // Skills
  skills: {
    list: () => apiClient.get('/skills'),
    getMyAcquired: () => apiClient.get('/skills/acquired/mine'),
    getMyRoleProfile: () => apiClient.get('/skills/profile/mine'),
    create: (skillData) => apiClient.post('/skills', skillData),
    update: (id, skillData) => apiClient.put(`/skills/${id}`, skillData),
    remove: (id) => apiClient.delete(`/skills/${id}`),
  },

  // Roles
  roles: {
    list: () => apiClient.get('/roles'),
    create: (roleData) => apiClient.post('/roles', roleData),
    update: (id, roleData) => apiClient.put(`/roles/${id}`, roleData),
    remove: (id) => apiClient.delete(`/roles/${id}`),
    // Super Admin: Master Admin's roles set up for their organization
    listForOrganization: () => apiClient.get('/roles/organization'),
    // permissions: "feature:action" list (see the access-control catalog); left out = unchanged
    saveForOrganization: (roleValue, menuIds, permissions) =>
      apiClient.put(`/roles/organization/${roleValue}`, { menu_ids: menuIds, ...(permissions !== undefined ? { permissions } : {}) }),
    removeFromOrganization: (roleValue) => apiClient.delete(`/roles/organization/${roleValue}`),
  },

  // Access Control
  accessControl: {
    getAll: () => apiClient.get('/access-control'),
    getByRole: (roleValue) => apiClient.get(`/access-control/role/${roleValue}`),
    // Feature permissions inside each panel's screens: { actions, panels: { org: [features] } }
    getCatalog: () => apiClient.get('/access-control/catalog'),
    upsert: (roleValue, menuIds, permissions) =>
      apiClient.put(`/access-control/${roleValue}`, { menu_ids: menuIds, ...(permissions !== undefined ? { permissions } : {}) }),
  },

  // Instructor
  instructor: {
    getDashboard: () => apiClient.get('/instructor/dashboard'),
    getCourses: () => apiClient.get('/instructor/courses'),
    getUsers: () => apiClient.get('/instructor/users'),
  },

  // Groups
  groups: {
    list: () => apiClient.get('/groups'),
    create: (groupData) => apiClient.post('/groups', groupData),
    update: (id, groupData) => apiClient.put(`/groups/${id}`, groupData),
    remove: (id) => apiClient.delete(`/groups/${id}`),

    getUsers: (id, organizationId = null) =>
      apiClient.get(`/groups/${id}/users`, {
        params: organizationId ? { organizationId } : {},
      }),

    setUsers: (id, userIds, organizationId = null) =>
      apiClient.put(`/groups/${id}/users`, {
        user_ids: userIds,
        ...(organizationId ? { organization_id: organizationId } : {}),
      }),

    getInstructors: (id, organizationId = null) =>
      apiClient.get(`/groups/${id}/instructors`, {
        params: organizationId ? { organizationId } : {},
      }),

    setInstructors: (id, userIds, organizationId = null) =>
      apiClient.put(`/groups/${id}/instructors`, {
        user_ids: userIds,
        ...(organizationId ? { organization_id: organizationId } : {}),
      }),
  },

  // Courses
  courses: {
    getAll: (adminView = false) =>
      apiClient.get(adminView ? '/courses/admin/all' : '/courses'),
    getById: (id) => apiClient.get(`/courses/${id}`),
    create: (courseData) => apiClient.post('/courses', courseData),
    update: (id, courseData) => apiClient.put(`/courses/${id}`, courseData),
    delete: (id) => apiClient.delete(`/courses/${id}`),
    restore: (id) => apiClient.put(`/courses/${id}/restore`),

    getCourseInstructors: (courseId, orgId = null) =>
      apiClient.get(
        `/courses/${courseId}/instructors${orgId ? `?org_id=${orgId}` : ''}`
      ),

    setCourseInstructors: (courseId, instructorIds) =>
      apiClient.put(`/courses/${courseId}/instructors`, {
        instructor_ids: instructorIds,
      }),
  },

  // Modules
  modules: {
    getByCourse: (courseId) => apiClient.get(`/modules/course/${courseId}`),
    getById: (id) => apiClient.get(`/modules/${id}`),
    create: (moduleData) => apiClient.post('/modules', moduleData),
    update: (id, moduleData) => apiClient.put(`/modules/${id}`, moduleData),
    delete: (id) => apiClient.delete(`/modules/${id}`),
    restore: (id) => apiClient.put(`/modules/${id}/restore`),
  },

  // Enrollments
  enrollments: {
    getAll: () => apiClient.get('/enrollments'),
    getByUser: (userId) => apiClient.get(`/enrollments/user/${userId}`),

    getByCourse: (courseId, options = {}) => {
      const params = {};

      if (options.orgId) {
        params.org_id = options.orgId;
      }

      if (options.includeDeleted) {
        params.includeDeleted = 'true';
      }

      return apiClient.get(`/enrollments/course/${courseId}`, { params });
    },

    create: (userId, courseId) =>
      apiClient.post('/enrollments', { userId, courseId }),

    delete: (id) => apiClient.delete(`/enrollments/${id}`),
    restore: (id) => apiClient.put(`/enrollments/${id}/restore`),

    enrollGroup: (courseId, groupId) =>
      apiClient.post(`/enrollments/course/${courseId}/enroll-group`, {
        group_id: groupId,
      }),

    enrollOrgGroup: (courseId, groupId, orgId) =>
      apiClient.post(`/enrollments/course/${courseId}/enroll-org-group`, {
        group_id: groupId,
        org_id: orgId,
      }),

    getOrgGroupPreview: (courseId, groupId, orgId) =>
      apiClient.get(
        `/enrollments/course/${courseId}/group-preview/${groupId}`,
        { params: { org_id: orgId } }
      ),

    getOrgAvailableLearners: (courseId, orgId) =>
      apiClient.get(
        `/enrollments/course/${courseId}/org-available-learners`,
        { params: { org_id: orgId } }
      ),

    enrollOrgIndividuals: (courseId, userIds, orgId) =>
      apiClient.post(
        `/enrollments/course/${courseId}/enroll-org-individuals`,
        { user_ids: userIds, org_id: orgId }
      ),
  },

  // Cart
  cart: {
    getItems: () => apiClient.get('/cart'),
    getCount: () => apiClient.get('/cart/count'),
    add: (courseId) => apiClient.post('/cart', { courseId }),
    remove: (courseId) => apiClient.delete(`/cart/${courseId}`),
  },

  // Payments
  payments: {
    initiate: (provider, voucherCode = null) =>
      apiClient.post('/payments/initiate', { provider, voucherCode }),

    complete: ({ paymentId, status, transactionId, details }) =>
      apiClient.post('/payments/complete', {
        paymentId,
        status,
        transactionId,
        details,
      }),

    getStatus: (paymentId) => apiClient.get(`/payments/${paymentId}/status`),
    listMy: () => apiClient.get('/payments/my'),

    capturePaypal: ({ paymentId, orderId }) =>
      apiClient.post('/payments/paypal/capture', { paymentId, orderId }),

    captureStripe: ({ paymentId, sessionId }) =>
      apiClient.post('/payments/stripe/capture', { paymentId, sessionId }),

    cancelStripe: ({ paymentId }) =>
      apiClient.post('/payments/stripe/cancel', { paymentId }),
  },

  // Vouchers
  vouchers: {
    list: (params = {}) => apiClient.get(`/vouchers${qsOf(params)}`),
    getById: (id) => apiClient.get(`/vouchers/${id}`),
    create: (data) => apiClient.post('/vouchers', data),
    update: (id, data) => apiClient.put(`/vouchers/${id}`, data),
    remove: (id) => apiClient.delete(`/vouchers/${id}`),
    restore: (id) => apiClient.post(`/vouchers/${id}/restore`),
    getUsage: (id, params = {}) =>
      apiClient.get(`/vouchers/${id}/usage${qsOf(params)}`),
  },

  // SCORM Tracking
  scormTracking: {
    // Get tracking data for a user, course, and module
    get: (userId, courseId, moduleId) => {
      if (!moduleId) {
        return apiClient.get(
          `/scorm-tracking/user/${userId}/course/${courseId}`
        );
      }

      return apiClient.get(
        `/scorm-tracking/${userId}/${courseId}/${moduleId}`
      );
    },

    // Get user tracking summary across all courses
    getUserSummary: (userId) =>
      apiClient.get(`/scorm-tracking/user/${userId}/summary`),

    getWeeklyStats: (userId) =>
      apiClient.get(`/scorm-tracking/user/${userId}/weekly-stats`),

    getMonthlySummary: (userId) =>
      apiClient.get(`/scorm-tracking/user/${userId}/monthly-summary`),

    myLearningRecord: () =>
      apiClient.get('/reports/my-learning-record', { responseType: 'blob' }),

    // Get all tracking data for a user and course
    getCourseTracking: (userId, courseId) =>
      apiClient.get(`/scorm-tracking/user/${userId}/course/${courseId}`),
  },

  // Organizations
  organizations: {
    getAll: () => apiClient.get('/orgs'),
    getById: (id) => apiClient.get(`/orgs/${id}`),
    getTeamsReadiness: (orgId) => apiClient.get(`/orgs/${orgId}/readiness/teams`),
    create: (orgData) => apiClient.post('/orgs', orgData),
    update: (id, orgData) => apiClient.put(`/orgs/${id}`, orgData),
    delete: (id) => apiClient.delete(`/orgs/${id}`),
    restore: (id) => apiClient.put(`/orgs/${id}/restore`),
    updateLoginSlides: (id, formData) =>
      apiClient.put(`/orgs/${id}/login-slides`, formData),

    set2faEnabled: (id, enabled) =>
      apiClient.patch(`/orgs/${id}/2fa`, { enabled: Boolean(enabled) }),

    setBrandingEnabled: (id, enabled) =>
      apiClient.patch(`/orgs/${id}/branding`, { enabled: Boolean(enabled) }),

    // [FEATURES] Features this user's organization has (used by FeaturesProvider)
    getMyFeatures: () => apiClient.get('/orgs/my-features'),
    getFeatureCatalog: () => apiClient.get('/orgs/feature-catalog'),

    // [FEATURES] Master / Super Admin: turn features on or off for an organization.
    // features example: { ethos_ai: true }
    updateFeatures: (id, features) =>
      apiClient.put(`/orgs/${id}/features`, { features }),

    getUsers: (id, includeDeleted = false) =>
      apiClient.get(`/orgs/${id}/users`, {
        params: { includeDeleted: includeDeleted ? 'true' : 'false' },
      }),

    addUser: (id, userDataOrId) =>
      apiClient.post(`/orgs/${id}/users`, userDataOrId),

    updateUser: (orgId, userId, userData) =>
      apiClient.put(`/orgs/${orgId}/users/${userId}`, userData),

    removeUser: (orgId, userId) =>
      apiClient.delete(`/orgs/${orgId}/users/${userId}`),

    restoreUser: (orgId, userId) =>
      apiClient.put(`/orgs/${orgId}/users/${userId}/restore`),

    bulkValidateUsers: (orgId, users) =>
      apiClient.post(`/orgs/${orgId}/users/bulk/validate`, { users }),

    bulkImportUsers: (orgId, users) =>
      apiClient.post(`/orgs/${orgId}/users/bulk/import`, { users }),

    getAdmins: (id, includeDeleted = false) =>
      apiClient.get(`/orgs/${id}/admins`, {
        params: { includeDeleted: includeDeleted ? 'true' : 'false' },
      }),

    addAdmin: (id, adminDataOrId) =>
      apiClient.post(`/orgs/${id}/admins`, adminDataOrId),

    updateAdmin: (orgId, adminId, adminData) =>
      apiClient.put(`/orgs/${orgId}/admins/${adminId}`, adminData),

    removeAdmin: (orgId, adminId) =>
      apiClient.delete(`/orgs/${orgId}/admins/${adminId}`),

    restoreAdmin: (orgId, adminId) =>
      apiClient.put(`/orgs/${orgId}/admins/${adminId}/restore`),

    getAllOrganizations: () => apiClient.get('/orgs'),

    getCourses: (id, includeDeleted = false) =>
      apiClient.get(`/orgs/${id}/courses`, {
        params: { includeDeleted: includeDeleted ? 'true' : 'false' },
      }),

    addCourse: (id, courseData) =>
      apiClient.post(`/orgs/${id}/courses`, courseData),

    updateCourse: (id, courseId, courseData) =>
      apiClient.put(`/orgs/${id}/courses/${courseId}`, courseData),

    removeCourse: (id, courseId) =>
      apiClient.delete(`/orgs/${id}/courses/${courseId}`),

    restoreCourse: (id, courseId) =>
      apiClient.put(`/orgs/${id}/courses/${courseId}/restore`),
  },

  // Licenses
  licenses: {
    getAllRequests: (status = null, organizationId = null) => {
      const params = {};

      if (status && status !== 'all') {
        params.status = status;
      }

      if (organizationId) {
        params.organization_id = organizationId;
      }

      return apiClient.get('/licenses/requests', { params });
    },

    getAllUsage: () => apiClient.get('/licenses/usage'),

    approveRequest: (id, approvedLicenses, notes = null) =>
      apiClient.put(`/licenses/requests/${id}/approve`, {
        approved_licenses: approvedLicenses,
        notes,
      }),

    rejectRequest: (id, rejectionReason, notes = null) =>
      apiClient.put(`/licenses/requests/${id}/reject`, {
        rejection_reason: rejectionReason,
        notes,
      }),

    getOrganizationRequests: (orgId, status = null) => {
      const params = {};

      if (status && status !== 'all') {
        params.status = status;
      }

      return apiClient.get(`/licenses/organizations/${orgId}/requests`, {
        params,
      });
    },

    createRequest: (orgId, requestedLicenses, notes = null) =>
      apiClient.post(`/licenses/organizations/${orgId}/requests`, {
        organization_id: orgId,
        requested_licenses: requestedLicenses,
        notes,
      }),

    getOrganizationUsage: (orgId) =>
      apiClient.get(`/licenses/organizations/${orgId}/usage`),

    checkAvailability: (orgId) =>
      apiClient.get(`/licenses/organizations/${orgId}/check`),

    consumeLicense: (orgId) =>
      apiClient.post(`/licenses/organizations/${orgId}/consume`),

    releaseLicense: (orgId) =>
      apiClient.post(`/licenses/organizations/${orgId}/release`),
  },

  // Support Tickets
  supportTickets: {
    getAll: (params = {}) =>
      apiClient.get(`/support-tickets?${new URLSearchParams(params)}`),

    getAllForAdmin: (params = {}) =>
      apiClient.get(`/support-tickets/admin/all?${new URLSearchParams(params)}`),

    getById: (id) => apiClient.get(`/support-tickets/${id}`),
    create: (ticketData) => apiClient.post('/support-tickets', ticketData),

    updateStatus: (id, statusData) =>
      apiClient.patch(`/support-tickets/${id}/status`, statusData),

    addMessage: (id, messageData) =>
      apiClient.post(`/support-tickets/${id}/messages`, messageData),

    uploadAttachment: (id, formData) =>
      apiClient.post(`/support-tickets/${id}/attachments`, formData, multipart),

    downloadAttachment: async (attachmentId) => {
      try {
        const response = await apiClient.get(
          `/support-tickets/attachments/${attachmentId}/download`,
          { responseType: 'blob' }
        );

        const contentDisposition = response.headers['content-disposition'];

        let filename = `attachment_${attachmentId}`;

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);

          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);

        return { success: true };
      } catch (error) {
        console.error('Download failed:', error);
        throw error;
      }
    },

    getStats: () => apiClient.get('/support-tickets/admin/stats'),
  },

  // Dashboard
  dashboard: {
    // Comprehensive dashboard statistics for Super Admin
    getStats: (params) => apiClient.get('/dashboard/stats', { params }),

    // Quick dashboard statistics
    getQuickStats: () => apiClient.get('/dashboard/quick-stats'),

    // Comprehensive dashboard statistics for Organization Admin
    getOrgStats: (params) => apiClient.get('/dashboard/org-stats', { params }),
  },

  // Manager (dashboard/reports + manager workspace, scoped to the manager's own reports)
  manager: {
    // ---- Manager dashboard / reports ----
    // Manager dashboard statistics
    getDashboardStats: () => apiClient.get('/dashboard/org-stats'),

    // Generic manager report
    getReport: (scope, section, params = {}) =>
      apiClient.get(`/reports/${scope}/${section}${qsOf(params)}`),

    // Manager learner/enrollment report
    getEnrollmentLearners: (scope, params = {}) =>
      apiClient.get(
        `/reports/${scope}/course-enrollments/learners${qsOf(params)}`
      ),

    // Backend-generated CSV report
    exportReport: (scope, section, params = {}) =>
      apiClient.get(
        `/reports/${scope}/${section}/export.csv${qsOf(params)}`,
        { responseType: 'blob' }
      ),

    // Manager notifications
    getNotifications: (params = {}) =>
      apiClient.get(`/notifications${qsOf(params)}`),

    // Manager calendar
    getCalendarEvents: (params = {}) =>
      apiClient.get(`/calendar/user/events${qsOf(params)}`),

    // ---- Manager workspace ----
    getScope: () =>
      apiClient.get('/manager/scope'),

    getTeam: () =>
      apiClient.get('/manager/team'),

    getTeamCandidates: (search = '') =>
      apiClient.get('/manager/team/candidates', { params: { search } }),

    addTeamMember: (userId) =>
      apiClient.post('/manager/team', { user_id: userId }),

    removeTeamMember: (userId) =>
      apiClient.delete(`/manager/team/${userId}`),

    getTeamReadiness: () => apiClient.get('/manager/readiness'),

    getGroups: () =>
      apiClient.get('/manager/groups'),

    createGroup: ({ name, purpose, memberIds }) =>
      apiClient.post('/manager/groups', {
        name,
        purpose,
        member_ids: memberIds
      }),

    updateGroup: (groupId, { name, purpose, memberIds }) =>
      apiClient.put(`/manager/groups/${groupId}`, {
        name,
        purpose,
        member_ids: memberIds
      }),

    deleteGroup: (groupId) =>
      apiClient.delete(`/manager/groups/${groupId}`),

    getAssignableCourses: () =>
      apiClient.get('/manager/courses'),

    assignLearning: ({ courseId, userIds, dueDate }) =>
      apiClient.post('/manager/assignments', {
        course_id: courseId,
        user_ids: userIds,
        due_date: dueDate || null
      }),

    getMyLearning: () =>
      apiClient.get('/manager/my-learning'),

    getCatalog: () =>
      apiClient.get('/manager/catalog'),

    getPaths: () =>
      apiClient.get('/manager/paths'),

    assignPath: ({ journeyId, userIds, dueDate }) =>
      apiClient.post('/manager/paths/assign', {
        journey_id: journeyId,
        user_ids: userIds,
        due_date: dueDate || null
      }),

    getSessions: () =>
      apiClient.get('/manager/sessions'),

    getSkillGaps: () =>
      apiClient.get('/manager/skill-gaps'),

    getEscalations: () =>
      apiClient.get('/manager/escalations'),

    // items: [{ user_id, key }]; a message is also sent to each report concerned
    acknowledgeEscalations: ({ items, message }) =>
      apiClient.post('/manager/escalations/acknowledge', { items, message: message || null }),

    sendNudges: ({ userIds, message }) =>
      apiClient.post('/manager/nudges', { user_ids: userIds, message }),

    getExceptionRequests: () =>
      apiClient.get('/manager/exception-requests'),

    createExceptionRequest: ({ userId, courseId, requestedDueDate, reason, note }) =>
      apiClient.post('/manager/exception-requests', {
        user_id: userId,
        course_id: courseId,
        requested_due_date: requestedDueDate,
        reason,
        note: note || null
      }),

    getSignoffs: () =>
      apiClient.get('/manager/signoffs'),

    // items: [{ user_id, course_id }]; decision 'signed_off' | 'returned' (a note is required to return)
    decideSignoffs: ({ items, decision, note }) =>
      apiClient.post('/manager/signoffs', { items, decision, note: note || null }),

    getSchedules: () =>
      apiClient.get('/manager/schedules'),

    saveSchedule: ({ kind, item, frequency, active = true, copyOrgAdmin = false }) =>
      apiClient.put('/manager/schedules', {
        kind,
        item,
        frequency,
        active,
        copy_org_admin: copyOrgAdmin
      }),

    deleteSchedule: (id) =>
      apiClient.delete(`/manager/schedules/${id}`),

    sendScheduleNow: ({ kind, item }) =>
      apiClient.post('/manager/schedules/send-now', { kind, item }),

    getProposals: (type = '') =>
      apiClient.get('/manager/proposals', { params: type ? { type } : {} }),

    // body: { type, title, description, details, submit }
    createProposal: (body) =>
      apiClient.post('/manager/proposals', body),

    updateProposal: (id, body) =>
      apiClient.put(`/manager/proposals/${id}`, body),

    deleteProposal: (id) =>
      apiClient.delete(`/manager/proposals/${id}`)
  },

  // Which licensed courses need a manager sign-off (org admin)
  signoffSettings: {
    list: () =>
      apiClient.get('/signoff-settings'),

    update: (courseId, required) =>
      apiClient.put(`/signoff-settings/${courseId}`, { required })
  },

  // Courses, paths and live sessions managers propose (org admin decides)
  proposals: {
    list: (status = '') =>
      apiClient.get('/proposals', { params: status ? { status } : {} }),

    decide: (id, { decision, note }) =>
      apiClient.put(`/proposals/${id}`, { decision, note: note || null })
  },

  // Deadline exception requests from managers (org admin decides)
  exceptionRequests: {
    list: (status = '') =>
      apiClient.get('/exception-requests', { params: status ? { status } : {} }),

    decide: (id, { decision, note }) =>
      apiClient.put(`/exception-requests/${id}`, { decision, note: note || null })
  },

  // Reports Analytics
  reports: {
    getSection: (scope, section, params = {}) =>
      apiClient.get(`/reports/${scope}/${section}${qsOf(params)}`),

    getEnrollmentLearners: (scope, params = {}) =>
      apiClient.get(
        `/reports/${scope}/course-enrollments/learners${qsOf(params)}`
      ),

    getOrgLicenseRequests: (scope, organizationId) =>
      apiClient.get(
        `/reports/${scope}/organizations/${organizationId}/license-requests`
      ),

    exportSection: (scope, section, params = {}) =>
      apiClient.get(
        `/reports/${scope}/${section}/export.csv${qsOf(params)}`,
        { responseType: 'blob' }
      ),
  },

  // Forums
  forums: {
    getAll: (params = {}) => apiClient.get(`/forums?${new URLSearchParams(params)}`),
    getById: (id) => apiClient.get(`/forums/${id}`),

    create: (forumData) => {
      if (forumData instanceof FormData) {
        console.log('🔍 API Client - Using FormData directly');

        for (const [key, value] of forumData.entries()) {
          console.log('🔍 FormData entry:', key, '=', value);
        }

        return apiClient.post('/forums', forumData, multipart);
      }

      const formData = new FormData();

      formData.append('title', forumData.title);
      formData.append('description', forumData.description);

      if (forumData.org_id) {
        formData.append('org_id', forumData.org_id);
      }

      if (forumData.attachment) {
        formData.append('attachment', forumData.attachment);
      }

      return apiClient.post('/forums', formData, multipart);
    },

    update: (id, forumData) => {
      if (forumData instanceof FormData) {
        return apiClient.put(`/forums/${id}`, forumData, multipart);
      }

      const formData = new FormData();

      if (forumData.title) {
        formData.append('title', forumData.title);
      }

      if (forumData.description) {
        formData.append('description', forumData.description);
      }

      if (forumData.org_id) {
        formData.append('org_id', forumData.org_id);
      }

      if (forumData.attachment) {
        formData.append('attachment', forumData.attachment);
      }

      return apiClient.put(`/forums/${id}`, formData, multipart);
    },

    delete: (id) => apiClient.delete(`/forums/${id}`),
    restore: (id) => apiClient.post(`/forums/${id}/restore`),

    getComments: (forumId, params = {}) =>
      apiClient.get(`/forums/${forumId}/comments?${new URLSearchParams(params)}`),

    createComment: (forumId, commentData) => {
      const formData = new FormData();

      formData.append('content', commentData.content);

      if (commentData.attachment) {
        formData.append('attachment', commentData.attachment);
      }

      return apiClient.post(`/forums/${forumId}/comments`, formData, multipart);
    },

    toggleLike: (forumId) => apiClient.post(`/forums/${forumId}/like`),

    toggleCommentLike: (commentId) =>
      apiClient.post(`/forums/comments/${commentId}/like`),

    highlightComment: (commentId, reason) =>
      apiClient.post(`/forums/comments/${commentId}/highlight`, { reason }),

    getHighlightedComments: (params = {}) =>
      apiClient.get(`/forums/moderation/highlighted?${new URLSearchParams(params)}`),

    moderateHighlightedComment: (highlightId, action) =>
      apiClient.post(`/forums/moderation/highlighted/${highlightId}`, { action }),
  },

  // VLT (Virtual-Led Training) API
  vlt: {
    getAll: (params = {}) => apiClient.get(`/vlt?${new URLSearchParams(params)}`),
    getById: (id) => apiClient.get(`/vlt/${id}`),

    create: (vltData) => {
      if (vltData instanceof FormData) {
        return apiClient.post('/vlt', vltData, multipart);
      }

      const formData = new FormData();

      formData.append('title', vltData.title);
      formData.append('description', vltData.description);
      formData.append('type', vltData.type);
      formData.append('url_or_path', vltData.url_or_path);

      if (vltData.course_id) {
        formData.append('course_id', vltData.course_id);
      }

      if (vltData.organization_id) {
        formData.append('organization_id', vltData.organization_id);
      }

      formData.append('status', vltData.status || 'active');

      if (vltData.thumbnail_image) {
        formData.append('thumbnail_image', vltData.thumbnail_image);
      }

      return apiClient.post('/vlt', formData, multipart);
    },

    update: (id, vltData) => {
      if (vltData instanceof FormData) {
        return apiClient.put(`/vlt/${id}`, vltData, multipart);
      }

      const formData = new FormData();

      if (vltData.title) {
        formData.append('title', vltData.title);
      }

      if (vltData.description) {
        formData.append('description', vltData.description);
      }

      if (vltData.type) {
        formData.append('type', vltData.type);
      }

      if (vltData.url_or_path) {
        formData.append('url_or_path', vltData.url_or_path);
      }

      if (vltData.course_id) {
        formData.append('course_id', vltData.course_id);
      }

      if (vltData.organization_id) {
        formData.append('organization_id', vltData.organization_id);
      }

      if (vltData.status) {
        formData.append('status', vltData.status);
      }

      if (vltData.thumbnail_image) {
        formData.append('thumbnail_image', vltData.thumbnail_image);
      }

      return apiClient.put(`/vlt/${id}`, formData, multipart);
    },

    delete: (id) => apiClient.delete(`/vlt/${id}`),
    restore: (id) => apiClient.post(`/vlt/${id}/restore`),
    assign: (assignmentData) => apiClient.post('/vlt/assign', assignmentData),

    getUserVlts: (params = {}) =>
      apiClient.get(`/vlt/user?${new URLSearchParams(params)}`),

    trackAccess: (vltId, action, metadata = {}) =>
      apiClient.post(`/vlt/${vltId}/track`, { action, metadata }),

    getReports: (params = {}) =>
      apiClient.get(`/vlt/reports?${new URLSearchParams(params)}`),

    getDocumentPreviewToken: (id) => apiClient.get(`/vlt/${id}/preview-token`),

    getInstructors: (organizationId = null) => {
      const query = organizationId ? `?organization_id=${organizationId}` : '';

      return apiClient.get(`/vlt/instructors/list${query}`);
    },
  },

  // Notifications
  notifications: {
    list: (params = {}) => apiClient.get(`/notifications${qsOf(params)}`),
    summary: () => apiClient.get('/notifications/summary'),
    markAllRead: () => apiClient.patch('/notifications/mark-all-read'),
    markRead: (notificationId) =>
      apiClient.patch(`/notifications/${notificationId}/read`),
    markUnread: (notificationId) =>
      apiClient.patch(`/notifications/${notificationId}/unread`),
  },

  // Certificate Management API
  certificates: {
    getTemplates: (params = {}) =>
      apiClient.get(`/certificates/templates?${new URLSearchParams(params)}`),

    getTemplate: (id) => apiClient.get(`/certificates/templates/${id}`),

    createTemplate: (templateData) =>
      apiClient.post('/certificates/templates', templateData),

    updateTemplate: (id, templateData) =>
      apiClient.put(`/certificates/templates/${id}`, templateData),

    deleteTemplate: (id) => apiClient.delete(`/certificates/templates/${id}`),

    previewTemplate: (id, sampleData = {}) =>
      apiClient.post(`/certificates/templates/${id}/preview`, sampleData),

    assignToCourse: (assignmentData) =>
      apiClient.post('/certificates/assign', assignmentData),

    updateAssignment: (id, assignmentData) =>
      apiClient.put(`/certificates/assign/${id}`, assignmentData),

    unassignFromCourse: (id) => apiClient.delete(`/certificates/assign/${id}`),

    getCourseAssignment: (courseId) =>
      apiClient.get(`/certificates/course/${courseId}`),

    getAllAssignments: (params = {}) =>
      apiClient.get(`/certificates/assignments?${new URLSearchParams(params)}`),

    generateCertificate: (generationData) =>
      apiClient.post('/certificates/generate', generationData),

    issueIfEligible: (userId, courseId) =>
      apiClient.post('/certificates/issue-if-eligible', {
        user_id: userId,
        course_id: courseId,
      }),

    renderImage: (payload) =>
      apiClient.post('/certificates/render-image', payload, {
        responseType: 'blob',
      }),

    getLearnerCertificates: (userId, params = {}) =>
      apiClient.get(
        `/certificates/learner/${userId}?${new URLSearchParams(params)}`
      ),

    downloadCertificate: (id) =>
      apiClient.get(`/certificates/download/${id}`, { responseType: 'blob' }),
  },

  // Surveys
  surveys: {
    getAll: (params = {}) =>
      apiClient.get(`/surveys?${new URLSearchParams(params)}`),

    getById: (id) => apiClient.get(`/surveys/${id}`),
    create: (formData) => apiClient.post('/surveys', formData, multipart),
    update: (id, formData) => apiClient.put(`/surveys/${id}`, formData, multipart),
    delete: (id) => apiClient.delete(`/surveys/${id}`),
    restore: (id) => apiClient.post(`/surveys/${id}/restore`),

    getQuestions: (surveyId) => apiClient.get(`/surveys/${surveyId}/questions`),
    getQuestionById: (id) => apiClient.get(`/surveys/questions/${id}`),

    createQuestion: (questionData) =>
      apiClient.post('/surveys/questions', questionData),

    bulkCreateQuestions: (questionData) =>
      apiClient.post('/surveys/questions/bulk', questionData),

    updateQuestion: (id, questionData) =>
      apiClient.put(`/surveys/questions/${id}`, questionData),

    deleteQuestion: (id) => apiClient.delete(`/surveys/questions/${id}`),

    reorderQuestions: (surveyId, questionOrders) =>
      apiClient.post('/surveys/questions/reorder', {
        survey_id: surveyId,
        question_orders: questionOrders,
      }),

    getAvailable: () => apiClient.get('/surveys/available/list'),
    getForResponse: (surveyId) => apiClient.get(`/surveys/${surveyId}/respond`),

    submitResponse: (responseData) =>
      apiClient.post('/surveys/responses/submit', responseData),

    getMyResponse: (surveyId) =>
      apiClient.get(`/surveys/responses/my/${surveyId}`),

    getResponses: (surveyId, params = {}) =>
      apiClient.get(
        `/surveys/${surveyId}/responses?${new URLSearchParams(params)}`
      ),

    getDetailedResponses: (surveyId) =>
      apiClient.get(`/surveys/${surveyId}/responses/detailed`),

    exportResponses: (surveyId) =>
      apiClient.get(`/surveys/${surveyId}/responses/export`, {
        responseType: 'blob',
      }),
  },

  // Calendar
  calendar: {
    getUserEvents: (params = {}) =>
      apiClient.get(`/calendar/user/events?${new URLSearchParams(params)}`),

    getOrgEvents: (params = {}) =>
      apiClient.get(`/calendar/org/events?${new URLSearchParams(params)}`),

    getInstructorEvents: (params = {}) =>
      apiClient.get(`/calendar/instructor/events?${new URLSearchParams(params)}`),

    getAdminEvents: (params = {}) =>
      apiClient.get(`/calendar/admin/events?${new URLSearchParams(params)}`),
  },

  // Assessments
  assessments: {
    create: (payload) =>
      apiClient.post('/assessments/admin/assessments/create', payload),

    getAll: () => apiClient.get('/assessments/admin/assessments'),
    getById: (id) => apiClient.get(`/assessments/admin/assessments/${id}`),

    update: (id, payload) =>
      apiClient.put(`/assessments/admin/assessments/${id}`, payload),

    delete: (id) => apiClient.delete(`/assessments/admin/assessments/${id}`),

    addQuestion: (payload) =>
      apiClient.post('/assessments/admin/questions/add', payload),

    updateQuestion: (id, payload) =>
      apiClient.put(`/assessments/admin/questions/${id}`, payload),

    deleteQuestion: (id) => apiClient.delete(`/assessments/admin/questions/${id}`),

    assign: (payload) =>
      apiClient.post('/assessments/admin/assignments/assign', payload),

    getAssignments: (assessmentId) =>
      apiClient.get('/assessments/admin/assignments', {
        params: assessmentId ? { assessment_id: assessmentId } : {},
      }),

    removeAssignment: (id) =>
      apiClient.delete(`/assessments/admin/assignments/${id}`),

    getAdminResults: (assessmentId) =>
      apiClient.get(`/assessments/admin/results/${assessmentId}`),

    getUserAssessments: () => apiClient.get('/assessments/user/assessments'),

    getUserAssessmentById: (id) =>
      apiClient.get(`/assessments/user/assessment/${id}`),

    start: (assessmentId) =>
      apiClient.post('/assessments/user/assessment/start', {
        assessment_id: assessmentId,
      }),

    saveAnswer: (payload) =>
      apiClient.post('/assessments/user/assessment/save-answer', payload),

    submit: (attemptId) =>
      apiClient.post('/assessments/user/assessment/submit', {
        attempt_id: attemptId,
      }),

    getUserResult: (assessmentId) =>
      apiClient.get(`/assessments/user/results/${assessmentId}`),
  },

  // Learning Journeys
  learningJourneys: {
    // Admin
    getAll: (params = {}) => apiClient.get('/learning-journeys/admin', { params }),
    getById: (id) => apiClient.get(`/learning-journeys/admin/${id}`),
    create: (formData) => apiClient.post('/learning-journeys/admin', formData),
    update: (id, formData) =>
      apiClient.put(`/learning-journeys/admin/${id}`, formData),
    delete: (id) => apiClient.delete(`/learning-journeys/admin/${id}`),

    addStep: (id, payload) =>
      apiClient.post(`/learning-journeys/admin/${id}/steps`, payload),

    updateStep: (id, stepId, payload) =>
      apiClient.put(`/learning-journeys/admin/${id}/steps/${stepId}`, payload),

    deleteStep: (id, stepId) =>
      apiClient.delete(`/learning-journeys/admin/${id}/steps/${stepId}`),

    reorderSteps: (id, ordered_step_ids) =>
      apiClient.put(`/learning-journeys/admin/${id}/steps/reorder`, {
        ordered_step_ids,
      }),

    getEnrollments: (id) =>
      apiClient.get(`/learning-journeys/admin/${id}/enrollments`),

    enroll: (id, payload) =>
      apiClient.post(`/learning-journeys/admin/${id}/enrollments`, payload),

    removeEnrollment: (id, enrollmentId) =>
      apiClient.delete(
        `/learning-journeys/admin/${id}/enrollments/${enrollmentId}`
      ),

    getReport: (id, type = 'progress') =>
      apiClient.get(`/learning-journeys/admin/${id}/reports`, {
        params: { type },
      }),

    exportReport: (id, type = 'progress', format = 'csv') =>
      apiClient.get(`/learning-journeys/admin/${id}/reports/export`, {
        params: { type, format },
        responseType: format === 'pdf' ? 'json' : 'blob',
      }),

    sendReminder: (id, payload) =>
      apiClient.post(`/learning-journeys/admin/${id}/reminders`, payload),

    // Learner
    getMyJourneys: () => apiClient.get('/learning-journeys/user'),
    getMyJourneyDetail: (id) => apiClient.get(`/learning-journeys/user/${id}`),

    startStep: (id, stepId) =>
      apiClient.post(`/learning-journeys/user/${id}/steps/${stepId}/start`),

    completeStep: (id, stepId, payload = {}) =>
      apiClient.post(
        `/learning-journeys/user/${id}/steps/${stepId}/complete`,
        payload
      ),
  },

  // Terms of Use
  terms: {
    listVersions: () => apiClient.get('/terms/versions'),
    createVersion: (payload) => apiClient.post('/terms/versions', payload),
    updateVersion: (id, payload) => apiClient.put(`/terms/versions/${id}`, payload),
    publishVersion: (id) => apiClient.post(`/terms/versions/${id}/publish`),
    getAcceptanceStatus: () => apiClient.get('/terms/acceptance-status'),
    acceptLatest: (meta = {}) => apiClient.post('/terms/accept', meta),
    getCurrent: () => apiClient.get('/terms/current'),
  },

  // Privacy Policy
  privacy: {
    listVersions: () => apiClient.get('/privacy/versions'),
    createVersion: (payload) => apiClient.post('/privacy/versions', payload),
    updateVersion: (id, payload) =>
      apiClient.put(`/privacy/versions/${id}`, payload),
    publishVersion: (id) => apiClient.post(`/privacy/versions/${id}/publish`),
    getCurrent: () => apiClient.get('/privacy/current'),
  },

  // FAQs
  faqs: {
    list: (params = {}) => apiClient.get(`/faqs${qsOf(params)}`),
    listPublic: (params = {}) => apiClient.get(`/faqs/public${qsOf(params)}`),
    create: (payload) => apiClient.post('/faqs', payload),
    update: (id, payload) => apiClient.put(`/faqs/${id}`, payload),

    reorder: (items, organizationId = null) =>
      apiClient.put('/faqs/reorder', { organization_id: organizationId, items }),

    remove: (id, organizationId = null) =>
      apiClient.delete(`/faqs/${id}`, {
        data: { organization_id: organizationId },
      }),
  },

  // Banners
  banners: {
    list: (params = {}) => apiClient.get(`/banners${qsOf(params)}`),
    create: (payload) => apiClient.post('/banners', payload),
    update: (id, payload) => apiClient.put(`/banners/${id}`, payload),

    reorder: (items, organizationId = null) =>
      apiClient.put('/banners/reorder', {
        organization_id: organizationId,
        items,
      }),

    remove: (id, organizationId = null) =>
      apiClient.delete(`/banners/${id}`, {
        data: { organization_id: organizationId },
      }),
  },

  // Features (landing page features, not the per-organization feature flags)
  features: {
    list: (params = {}) => apiClient.get(`/features${qsOf(params)}`),
    create: (payload) => apiClient.post('/features', payload),
    update: (id, payload) => apiClient.put(`/features/${id}`, payload),

    reorder: (items, organizationId = null) =>
      apiClient.put('/features/reorder', {
        organization_id: organizationId,
        items,
      }),

    remove: (id, organizationId = null) =>
      apiClient.delete(`/features/${id}`, {
        data: { organization_id: organizationId },
      }),
  },

  // Awards
  awards: {
    list: (params = {}) => apiClient.get(`/awards${qsOf(params)}`),
    create: (payload) => apiClient.post('/awards', payload),
    update: (id, payload) => apiClient.put(`/awards/${id}`, payload),

    reorder: (items, organizationId = null) =>
      apiClient.put('/awards/reorder', {
        organization_id: organizationId,
        items,
      }),

    remove: (id, organizationId = null) =>
      apiClient.delete(`/awards/${id}`, {
        data: { organization_id: organizationId },
      }),
  },

  // Testimonials
  testimonials: {
    list: (params = {}) => apiClient.get(`/testimonials${qsOf(params)}`),
    create: (payload) => apiClient.post('/testimonials', payload),
    update: (id, payload) => apiClient.put(`/testimonials/${id}`, payload),

    reorder: (items, organizationId = null) =>
      apiClient.put('/testimonials/reorder', {
        organization_id: organizationId,
        items,
      }),

    remove: (id, organizationId = null) =>
      apiClient.delete(`/testimonials/${id}`, {
        data: { organization_id: organizationId },
      }),
  },

  // Landing Page
  landingPage: {
    getConfig: () => apiClient.get('/landing-page'),
    saveConfig: (payload) => apiClient.post('/landing-page', payload),
  },

  // Gamification
  gamification: {
    getConfig: () => apiClient.get('/gamification/config'),

    updateActivity: (id, payload) =>
      apiClient.put(`/gamification/activities/${id}`, payload),

    updateBadgeLevel: (id, payload) =>
      apiClient.put(`/gamification/badge-levels/${id}`, payload),

    getLeaderboard: (params = {}) =>
      apiClient.get(`/gamification/leaderboard${qsOf(params)}`),

    getMyProfile: () => apiClient.get('/gamification/my-profile'),
    acknowledgeLevelUp: () => apiClient.post('/gamification/acknowledge-level-up'),
  },

  // Readiness
  readiness: {
    getMine: () => apiClient.get('/readiness/me'),
    getUsers: (ids) => apiClient.get('/readiness/users', { params: { ids: ids.join(',') } }),
    getOrgs: (ids) =>
      apiClient.get('/readiness/orgs', { params: { ids: ids.join(',') } }),
  },

  getBriefing: () => api.get('/ai/briefing'),

  // EduGenie
  edugenie: {
    getConfig: () => apiClient.get('/edugenie/config'),
    createSession: () => apiClient.post('/edugenie/session'),

    listSessions: (params = {}) =>
      apiClient.get(`/edugenie/sessions${qsOf(params)}`),

    getHistory: (params = {}) =>
      apiClient.get(`/edugenie/history${qsOf(params)}`),

    clearHistory: (params = {}) =>
      apiClient.delete(`/edugenie/history${qsOf(params)}`),

    getConversation: (id) => apiClient.get(`/edugenie/conversations/${id}`),
    chat: (payload) => apiClient.post('/edugenie/chat', payload),
    submitFeedback: (payload) => apiClient.post('/edugenie/feedback', payload),

    getAdminStatus: () => apiClient.get('/edugenie/admin/status'),
    testIntent: (payload) => apiClient.post('/edugenie/admin/test-intent', payload),
    getAnalytics: () => apiClient.get('/edugenie/admin/analytics'),
    listSettings: () => apiClient.get('/edugenie/admin/settings'),
    upsertSetting: (payload) => apiClient.put('/edugenie/admin/settings', payload),

    listIntents: (params = {}) =>
      apiClient.get(`/edugenie/admin/intents${qsOf(params)}`),

    createIntent: (payload) => apiClient.post('/edugenie/admin/intents', payload),

    updateIntent: (id, payload) =>
      apiClient.put(`/edugenie/admin/intents/${id}`, payload),

    deleteIntent: (id) => apiClient.delete(`/edugenie/admin/intents/${id}`),

    listKeywords: (params = {}) =>
      apiClient.get(`/edugenie/admin/keywords${qsOf(params)}`),

    createKeyword: (payload) => apiClient.post('/edugenie/admin/keywords', payload),

    updateKeyword: (id, payload) =>
      apiClient.put(`/edugenie/admin/keywords/${id}`, payload),

    deleteKeyword: (id) => apiClient.delete(`/edugenie/admin/keywords/${id}`),

    listConversations: (params = {}) =>
      apiClient.get(`/edugenie/admin/conversations${qsOf(params)}`),

    listFeedback: (params = {}) =>
      apiClient.get(`/edugenie/admin/feedback${qsOf(params)}`),

    listUnknownQuestions: (params = {}) =>
      apiClient.get(`/edugenie/admin/unknown-questions${qsOf(params)}`),

    answerUnknownQuestion: (id, payload) =>
      apiClient.post(`/edugenie/admin/unknown-questions/${id}/answer`, payload),

    ignoreUnknownQuestion: (id) =>
      apiClient.post(`/edugenie/admin/unknown-questions/${id}/ignore`),

    listQuickActions: () => apiClient.get('/edugenie/admin/quick-actions'),

    updateQuickAction: (id, payload) =>
      apiClient.put(`/edugenie/admin/quick-actions/${id}`, payload),
  },

  // Master Admin
  master: {
    getPlans: () => apiClient.get('/master/plans'),
    getCourses: (params) => apiClient.get('/master/courses', { params }),
    setCourseOrganizations: (courseId, organizationIds) =>
      apiClient.put(`/master/courses/${courseId}/organizations`, { organization_ids: organizationIds }),
    getRoleCounts: () => apiClient.get('/master/role-counts'),
    getSuperAdmins: () => apiClient.get('/master/super-admins'),
    getPortfolioDashboard: () => apiClient.get('/master/portfolio-dashboard'),
    getOrgsReadiness: () => apiClient.get('/master/orgs-readiness'),

    checkTenant: (slug) =>
      apiClient.get('/master/tenants/check', { params: { slug } }),

    provisionOrg: (payload) => apiClient.post('/master/orgs/provision', payload),
  },
};

// Direct axios-style methods for backward compatibility
api.get = (url, config) => apiClient.get(url, config);
api.post = (url, data, config) => apiClient.post(url, data, config);
api.put = (url, data, config) => apiClient.put(url, data, config);
api.delete = (url, config) => apiClient.delete(url, config);
api.patch = (url, data, config) => apiClient.patch(url, data, config);

// Export both the structured API and raw axios instance for flexibility
export { apiClient as axiosInstance };

export default api;